import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt';
import { FullCalendarModule } from '@fullcalendar/angular';
import { INDEXDB_STORE_WIDGET } from '@jms/model';
import { CoreDataModule } from '@jms/tenant/module.jms.core.data';
import { NgrxActionsModule } from '@jms/shared/ngrx-actions';
import { NgrxSelect } from '@jms/shared/ngrx-actions';
import { AvatarModule } from '@jms/ui/ui.avatar';
import { ConfirmModule } from '@jms/ui/ui.confirm';
import { ErrorPageModule } from '@jms/ui/ui.error.pages';
import { UploaderModule, uploaderReducer } from '@jms/ui/ui.uploader';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';
import { PipeModule } from '@jms/utils/util.pipes';
import { ReduxModule } from '@jms/utils/util.redux';
import { SentryModule, SENTRY_IGNORED_HTTP_STATUSES } from '@jms/utils/util.sentry.io';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

import { DialogModule } from 'primeng/dialog';
import { ProgressBarModule } from 'primeng/progressbar';
import { FaviconModule } from '@jms/ui/ng-favicon';
import { DBConfig, NgxIndexedDBModule } from 'ngx-indexed-db';
import { PortalModule } from '@angular/cdk/portal';
import { OverlayModule } from '@angular/cdk/overlay';
import { NgxStripeModule } from 'ngx-stripe';
import { NgMapsCoreModule } from '@ng-maps/core';
import { NgMapsGoogleModule } from '@ng-maps/google';
import { MenuModule } from 'primeng/menu';
import { ModuleRegistry, AllCommunityModule, ClientSideRowModelModule, GroupFilterModule, PivotModule, RowGroupingModule, RowGroupingPanelModule, TreeDataModule } from 'ag-grid-enterprise';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { ApplicationEffects, applicationReducer } from '@jms/tenant/module.jms.state';
import { ToastModule } from 'primeng/toast';

// #region IndexDB configuration

const indexDbConfig: DBConfig = {
    name: 'JmsDb',
    version: 2,
    objectStoresMeta: [{
        store: INDEXDB_STORE_WIDGET,
        storeConfig: { keyPath: 'id', autoIncrement: false },
        storeSchema: [
            { name: 'expired', keypath: 'expired', options: { unique: false } },
            { name: 'lastAccessed', keypath: 'lastAccessed', options: { unique: false } },
        ]
    }],
};

// #endregion

// #region NgRx Store configuration

const storeConfig = {
    runtimeChecks: {
        strictStateSerializability: false,
        strictStateImmutability: false,
        strictActionSerializability: false,
        strictActionImmutability: false,
    }
};

// #endregion

// #region AG Grid registration

ModuleRegistry.registerModules([
    AllCommunityModule,
    ClientSideRowModelModule,
    GroupFilterModule,
    PivotModule,
    RowGroupingModule,
    RowGroupingPanelModule,
    TreeDataModule
]);

// #endregion

@NgModule({
    imports: [
        // Core Angular
        BrowserModule,
        ReactiveFormsModule,

        // NgRx
        StoreModule.forRoot({
            application: applicationReducer,
            uploader: uploaderReducer
        }, storeConfig),
        StoreDevtoolsModule.instrument({ maxAge: 10 }),
        StoreRouterConnectingModule.forRoot({ stateKey: 'application' }),
        EffectsModule.forRoot([ApplicationEffects]),
        NgrxActionsModule,

        // 3rd Party Modules
        FullCalendarModule,
        SentryModule.forRoot({ blacklistCodes: SENTRY_IGNORED_HTTP_STATUSES }),
        JwtModule.forRoot({
            jwtOptionsProvider: {
                provide: JWT_OPTIONS,
                useFactory: () => ({
                    authScheme: '',
                    tokenGetter: () => localStorage.getItem('auth_token'),
                    skipWhenExpired: true,
                    allowedDomain: new Array(new RegExp('^null$'))
                })
            }
        }),
        NgxIndexedDBModule.forRoot(indexDbConfig),
        NgxStripeModule.forRoot(ConfigService.environment.stripe),
        NgMapsCoreModule,
        NgMapsGoogleModule,
        MonacoEditorModule.forRoot(),

        // UI Modules
        DialogModule,
        ProgressBarModule,
        ToastModule,
        MenuModule,
        PortalModule,
        OverlayModule,

        // JMS Modules
        CoreDataModule.forRoot(),
        ReduxModule.forRoot(),
        PipeModule,
        AvatarModule,
        ErrorPageModule,
        ConfirmModule,
        UploaderModule,
        FaviconModule,
    ]
})
export class ApplicationModule {

    constructor(
        store: Store<any>,
        ngrxSelect: NgrxSelect,
    ) {
        ngrxSelect.connect(store);
    }
}
